<template>
  <div class="position-relative">
    <slot v-if="isDataLoading || isDataComputing" name="loading"></slot>

    <div
      v-if="data.length === 0 || (rows.length === 0 && cols.length === 0)"
      class="no-data-text"
      >
      <p v-if="isDataLoading">
        {{ loadingText }}
      </p>
      <p v-else class="alert alert-warning" role="alert">
        {{ noDataWarningText }}
      </p>
      </div>

      <div v-else-if="cols.length && rows.length" class="table-responsive">
      <table class="table 'table-bordered'" :aria-busy="isDataLoading || isDataComputing">

          <!-- Table header -->
          <thead>
            <template v-for="({ colField, cols }, colFieldIndex) in tableHeader.colHeaderFields">
              <!-- Multiple slots -->
              <template v-if="colField.headerSlotNames">
                <tr
                  v-for="(headerSlotName, headerSlotNameIndex) in colField.headerSlotNames"
                  :key="`${colFieldIndex}-${headerSlotNameIndex}`">
                  <!-- Top left dead zone -->
                  <slot
                    v-if="colFieldIndex === 0 && rowHeaderSize > 0 && headerSlotNameIndex === 0"
                    name="top-left"
                    :colspan="rowHeaderSize"
                    :rowspan="colHeaderSize"
                  >
                    <th
                      :colspan="rowHeaderSize"
                      :rowspan="colHeaderSize">
                    </th>
                  </slot>
                  <!-- Column headers -->
                  <template
                    v-for="(col, colIndex) in cols"
                  >
                    <slot
                      v-if="headerSlotName"
                      :name="headerSlotName"
                      :value="col.value"
                      :colspan="col.colspan"
                      :colIndex="colIndex"
                      :items="mergeItems(sortedCols, sumColspans(cols, colIndex), sumColspans(cols, colIndex) + col.colspan)"
                    >
                      Missing slot <code>{{ headerSlotName }}</code>
                    </slot>
                    <th
                      v-else
                      :key="`${colFieldIndex}-${headerSlotNameIndex}-${colIndex}`"
                      :colspan="col.colspan"
                    >
                      {{ col.value }}
                    </th>
                  </template>
                  <!-- Top right dead zone -->
                  <slot
                    v-if="colFieldIndex === 0 && rowFooterSize > 0 && headerSlotNameIndex === 0"
                    name="top-right"
                    :colspan="rowFooterSize"
                    :rowspan="colHeaderSize"
                  >
                    <th
                      :colspan="rowFooterSize"
                      :rowspan="colHeaderSize">
                    </th>
                  </slot>
                </tr>
              </template>
              <!-- Single slot/no slot -->
              <tr v-else :key="colFieldIndex">
                <!-- Top left dead zone -->
                <slot
                  v-if="colFieldIndex === 0 && rowHeaderSize > 0"
                  name="top-left"
                  :colspan="rowHeaderSize"
                  :rowspan="colHeaderSize"
                >
                  <th
                    :colspan="rowHeaderSize"
                    :rowspan="colHeaderSize">
                  </th>
                </slot>
                <!-- Column headers -->
                <template
                  v-for="(col, colIndex) in cols"
                >
                  <slot
                    v-if="colField.headerSlotName"
                    :name="colField.headerSlotName"
                    :value="col.value"
                    :colspan="col.colspan"
                    :colIndex="colIndex"
                    :items="mergeItems(sortedCols, sumColspans(cols, colIndex), sumColspans(cols, colIndex) + col.colspan)"
                  >
                    Missing slot <code>{{ colField.headerSlotName }}</code>
                  </slot>
                  <th
                    v-else
                    :key="`${colFieldIndex}-${colIndex}`"
                    :colspan="col.colspan">
                      {{ col.value }}
                  </th>
                </template>
                <!-- Top right dead zone -->
                <slot
                  v-if="colFieldIndex === 0 && rowFooterSize > 0"
                  name="top-right"
                  :colspan="rowFooterSize"
                  :rowspan="colHeaderSize"
                >
                  <th
                    :colspan="rowFooterSize"
                    :rowspan="colHeaderSize">
                  </th>
                </slot>
              </tr>
            </template>
          </thead>

          <!-- Table body -->
          <tbody>
            <tr v-for="({ row, rowHeaderFields, rowFooterFields }, rowIndex) in tableRows" :key="rowIndex">
              <template v-if="isRowVisible(rowIndex) || spanCheck(rowHeaderFields)">
                <!-- Row headers -->
                <template v-for="(rowField, rowFieldIndex) in rowHeaderFields">
                  <!-- Multiple slots -->
                  <template v-if="rowField.headerSlotNames">
                    <template
                      v-for="(headerSlotName) in rowField.headerSlotNames"
                    >
                      <slot
                        :name="headerSlotName"
                        :value="rowField.value"
                        :rowspan="rowField.rowspan"
                        :rowIndex="rowIndex"
                        :items="mergeItems(sortedRows, rowIndex, rowIndex + rowField.rowspan)"
                      >
                        Missing slot <code>{{ headerSlotName }}</code>
                      </slot>
                    </template>
                  </template>
                  <!-- Single slot/no slot -->
                  <template v-else>
                    <slot
                      v-if="rowField.headerSlotName"
                      :name="rowField.headerSlotName"
                      :value="rowField.value"
                      :rowspan="rowField.rowspan"
                      :rowIndex="rowIndex"
                      :items="mergeItems(sortedRows, rowIndex, rowIndex + rowField.rowspan)"
                    >
                      Missing slot <code>{{ rowField.headerSlotName }}</code>
                    </slot>
                    <th
                      v-else
                      :key="`header-${rowIndex}-${rowFieldIndex}`"
                      :rowspan="rowField.rowspan"
                    >
                      {{ rowField.value }}
                    </th>
                  </template>
                </template>

                <!-- Values -->
                <template v-for="(col, colIndex) in sortedCols">
                  <slot
                    v-if="$scopedSlots.value"
                    name="value"
                    :value="value(row.key, col.key)"
                    :labels="labels(row.key, col.key)"
                    :items="items(row.key, col.key)"
                    :rowIndex="rowIndex"
                    :colIndex="colIndex"
                  />
                  <td v-else
                    :key="`value-${rowIndex}-${colIndex}`"
                    class="text-right">
                    {{ value(row.key, col.key) }}
                  </td>
                </template>

                <!-- Row footers (if slots are provided) -->
                <template v-for="(rowField, rowFieldIndex) in rowFooterFields">
                  <!-- Multiple slots -->
                  <template v-if="rowField.footerSlotNames">
                    <template
                      v-for="(footerSlotName) in rowField.footerSlotNames"
                    >
                      <slot
                        :name="footerSlotName"
                        :value="rowField.value"
                        :rowspan="rowField.rowspan"
                        :rowIndex="rowIndex"
                        :items="mergeItems(sortedRows, rowIndex, rowIndex + rowField.rowspan)"
                      >
                        Missing slot <code>{{ footerSlotName }}</code>
                      </slot>
                    </template>
                  </template>
                  <!-- Single slot/no slot -->
                  <template
                    v-else
                  >
                    <slot
                      v-if="rowField.footerSlotName"
                      :name="rowField.footerSlotName"
                      :value="rowField.value"
                      :rowspan="rowField.rowspan"
                      :rowIndex="rowIndex"
                      :items="mergeItems(sortedRows, rowIndex, rowIndex + rowField.rowspan)"
                    >
                      Missing slot <code>{{ rowField.footerSlotName }}</code>
                    </slot>
                    <th
                      v-else
                      :key="`footer-${rowIndex}-${rowFieldIndex}`"
                      :rowspan="rowField.rowspan"
                    >
                      {{ rowField.value }}
                    </th>
                  </template>
                </template>
              </template>
            </tr>
          </tbody>

          <!-- Table footer -->
          <tfoot>
            <template v-for="({ colField, cols }, colFieldIndex) in tableFooter.colFooterFields">
              <!-- Multiple slots -->
              <template v-if="colField.footerSlotNames">
                <tr
                  v-for="(footerSlotName, footerSlotNameIndex) in colField.footerSlotNames"
                  :key="`${colFieldIndex}-${footerSlotNameIndex}`">
                  <!-- Bottom left dead zone -->
                  <slot
                    v-if="colFieldIndex === 0 && rowHeaderSize > 0 && footerSlotNameIndex === 0"
                    name="bottom-left"
                    :colspan="rowHeaderSize"
                    :rowspan="colFooterSize"
                  >
                    <th
                      :colspan="rowHeaderSize"
                      :rowspan="colFooterSize">
                    </th>
                  </slot>
                  <!-- Column footers -->
                  <template v-for="(col, colIndex) in cols">
                    <slot
                      :name="footerSlotName"
                      :value="col.value"
                      :colspan="col.colspan"
                      :colIndex="colIndex"
                      :items="mergeItems(sortedCols, sumColspans(cols, colIndex), sumColspans(cols, colIndex) + col.colspan)"
                    >
                      Missing slot <code>{{ footerSlotName }}</code>
                    </slot>
                  </template>
                  <!-- Bottom right dead zone -->
                  <slot
                    v-if="colFieldIndex === 0 && rowFooterSize > 0 && footerSlotNameIndex === 0"
                    name="bottom-right"
                    :colspan="rowFooterSize"
                    :rowspan="colFooterSize"
                  >
                    <th
                      :colspan="rowFooterSize"
                      :rowspan="colFooterSize">
                    </th>
                  </slot>
                </tr>
              </template>
              <!-- Single slot/no slot -->
              <tr v-else :key="colFieldIndex">
                <!-- Bottom left dead zone -->
                <slot
                  v-if="colFieldIndex === 0 && rowHeaderSize > 0"
                  name="bottom-left"
                  :colspan="rowHeaderSize"
                  :rowspan="colFooterSize"
                >
                  <th
                    :colspan="rowHeaderSize"
                    :rowspan="colFooterSize">
                  </th>
                </slot>
                <!-- Column footers -->
                <template
                  v-for="(col, colIndex) in cols"
                >
                  <slot
                    v-if="colField.footerSlotName"
                    :name="colField.footerSlotName"
                    :value="col.value"
                    :colspan="col.colspan"
                    :colIndex="colIndex"
                    :items="mergeItems(sortedCols, sumColspans(cols, colIndex), sumColspans(cols, colIndex) + col.colspan)"
                  >
                    Missing slot <code>{{ colField.footerSlotName }}</code>
                  </slot>
                  <th
                    v-else
                    :key="`${colFieldIndex}-${colIndex}`"
                    :colspan="col.colspan"
                  >
                    {{ col.value }}
                  </th>
                </template>
                <!-- Bottom right dead zone -->
                <slot
                    v-if="colFieldIndex === 0 && rowFooterSize > 0"
                  name="bottom-right"
                  :colspan="rowFooterSize"
                  :rowspan="colFooterSize"
                >
                  <th
                    :colspan="rowFooterSize"
                    :rowspan="colFooterSize">
                  </th>
                </slot>
              </tr>
            </template>
          </tfoot>
        </table>
      </div>
  </div>
</template>

<script>
import ManyKeysMap from 'many-keys-map'
import { firstBy } from 'thenby'
import naturalSort from 'javascript-natural-sort'
import cloneDeep from 'lodash-es/cloneDeep'
import isEqual from 'lodash-es/isEqual'
import omit from 'lodash.omit';

const isUndefinedOrNull = (value) => value === undefined || value === null;

export default {
  props: {
    data: {
      type: Array,
      default: () => []
    },
    rowFields: {
      type: Array,
      default: () => []
    },
    colFields: {
      type: Array,
      default: () => []
    },
    reducer: {
      type: Function,
      default: sum => sum + 1
    },
    reducerInitialValue: {
      default: 0
    },
    noDataWarningText: {
      type: String,
      default: 'No data to display.'
    },
    loadingText: {
      type: String,
      default: 'Loading...'
    },
    isDataLoading: {
      type: Boolean,
      default: false
    },
    virtualStart: {
      type: Number,
    },
    virtualEnd: {
      type: Number,
    },
    editingIndex: {
      type: Number,
    }
  },
  data: function() {
    return {
      valuesMap: null,
      labelsMap: null,
      itemsMap: null,
      rows: [],
      cols: [],
      // Note: we don't use directly rowFields/colFields props to trigger table render when `updateValues` has finished
      internalRowFields: [],
      internalColFields: [],
      isDataComputing: false,
      computingInterval: null
    }
  },
  computed: {
    // Sort rows/cols using a composed function built with thenBy.js
    sortedRows: function() {
      let composedSortFunction
      this.internalRowFields.forEach((rowField, rowFieldIndex) => {
        if (rowFieldIndex === 0) {
          composedSortFunction = firstBy(0, { cmp: rowField.sort || naturalSort })
        } else {
          composedSortFunction = composedSortFunction.thenBy(rowFieldIndex, { cmp: rowField.sort || naturalSort })
        }
      })

      return [...this.rows].sort(({ key: keya }, { key: keyb }) => composedSortFunction(keya, keyb))
    },
    sortedCols: function() {
      let composedSortFunction
      this.internalColFields.forEach((colField, colFieldIndex) => {
        if (colFieldIndex === 0) {
          composedSortFunction = firstBy(0, { cmp: colField.sort || naturalSort })
        } else {
          composedSortFunction = composedSortFunction.thenBy(colFieldIndex, { cmp: colField.sort || naturalSort })
        }
      })

      return [...this.cols].sort(({ key: keya }, { key: keyb }) => composedSortFunction(keya, keyb))
    },
    // Property to watch specific field changes
    fields: function() {
      return [
        [this.colFields, this.rowFields],
        [this.colFields.map(field => field.headerSlotNames), this.rowFields.map(field => field.headerSlotNames)],
        [this.colFields.map(field => field.valuesFiltered), this.rowFields.map(field => field.valuesFiltered)]
      ]
    },
    // Number of row header columns
    rowHeaderSize: function() {
      return this.internalRowFields.reduce((acc, rowField) => {
        if (rowField.showHeader === undefined || rowField.showHeader) {
          if (rowField.headerSlotNames) return acc + rowField.headerSlotNames.length
          else return acc + 1
        }
        else return acc
      }, 0)
    },
    // Number of row footer columns
    rowFooterSize: function() {
      return this.internalRowFields.reduce((acc, rowField) => {
        if (rowField.showFooter) {
          if (rowField.footerSlotNames) return acc + rowField.footerSlotNames.length
          else return acc + 1
        }
        else return acc
      }, 0)
    },
    // Number of col header rows
    colHeaderSize: function() {
      return this.internalColFields.reduce((acc, colField) => {
        if (colField.showHeader === undefined || colField.showHeader) {
          if (colField.headerSlotNames) return acc + colField.headerSlotNames.length
          else return acc + 1
        }
        else return acc
      }, 0)
    },
    // Number of col footer rows
    colFooterSize: function() {
      return this.internalColFields.reduce((acc, colField) => {
        if (colField.showFooter) {
          if (colField.footerSlotNames) return acc + colField.footerSlotNames.length
          else return acc + 1
        }
        else return acc
      }, 0)
    },
    // Table header
    tableHeader: function() {
      const colHeaderFields = this.internalColFields.map((colField, colFieldIndex) => {
        return {
          colField,
          cols: this.sortedCols.map((col, colIndex) => ({
            value: col.key[colFieldIndex],
            colspan: this.spanSize(this.sortedCols, colIndex, colFieldIndex)
          })).filter(colField => colField.colspan !== 0)
        }
      }).filter(({ colField }) => colField.showHeader === undefined || colField.showHeader)

      return {
        colHeaderFields
      }
    },
    // Table footer
    tableFooter: function() {
      const colFooterFields = this.internalColFields.map((colField, colFieldIndex) => {
        return {
          colField,
          cols: this.sortedCols.map((col, colIndex) => ({
            value: col.key[colFieldIndex],
            colspan: this.spanSize(this.sortedCols, colIndex, colFieldIndex)
          })).filter(colField => colField.colspan !== 0)
        }
      }).filter(({ colField }) => colField.showFooter).reverse()

      return {
        colFooterFields
      }
    },
    // Table rows with row headers/footers with rowspan
    tableRows: function() {
      return this.sortedRows.map((row, rowIndex) => {
        const rowHeaderFields = this.internalRowFields.map((rowField, rowFieldIndex) => ({
          ...rowField,
          value: row.key[rowFieldIndex],
          rowspan: this.spanSize(this.sortedRows, rowIndex, rowFieldIndex)
        })).filter(rowField => (rowField.showHeader === undefined || rowField.showHeader) && rowField.rowspan !== 0)

        const rowFooterFields = this.internalRowFields.map((rowField, rowFieldIndex) => ({
          ...rowField,
          value: row.key[rowFieldIndex],
          rowspan: this.spanSize(this.sortedRows, rowIndex, rowFieldIndex)
        })).filter(rowField => rowField.showFooter && rowField.rowspan !== 0).reverse()

        return {
          row,
          rowHeaderFields,
          rowFooterFields
        }
      })
    },
    // Table cols with col headers/footers with colspan
    tableCols: function() {
      return this.sortedCols.map((col, colIndex) => {
        const colHeaderFields = this.internalColFields.map((colField, colFieldIndex) => ({
          ...colField,
          value: col.key[colFieldIndex],
          colspan: this.spanSize(this.sortedCols, colIndex, colFieldIndex)
        })).filter(colField => (colField.showHeader === undefined || colField.showHeader) && colField.colspan !== 0)

        const colFooterFields = this.internalColFields.map((colField, colFieldIndex) => ({
          ...colField,
          value: col.key[colFieldIndex],
          colspan: this.spanSize(this.sortedCols, colIndex, colFieldIndex)
        })).filter(colField => colField.showFooter && colField.colspan !== 0).reverse()

        return {
          col,
          colHeaderFields,
          colFooterFields
        }
      })
    }
  },
  methods: {
    // Get value from valuesMap
    value: function(row, col) {
      const value = this.valuesMap.get([...row, ...col])
      return !isUndefinedOrNull(value) ? value : this.reducerInitialValue
    },
    // Get labels for a cell
    labels: function(row, col) {
      return this.labelsMap.get([...row, ...col]) || []
    },
    items: function(row, col) {
      return this.itemsMap.get([...row, ...col]) || []
    },
    // Get colspan/rowspan size
    spanSize: function(values, valueIndex, fieldIndex) {
      // If left value === current value
      // and top value === 0 (= still in the same top bracket)
      // The left td will take care of the display
      if (valueIndex > 0 &&
        values[valueIndex - 1].key[fieldIndex] === values[valueIndex].key[fieldIndex] &&
        (fieldIndex === 0 || (this.spanSize(values, valueIndex, fieldIndex - 1) === 0))) {
        return 0
      }

      // Otherwise, count entries on the right with the same value
      // But stop if the top value !== 0 (= the top bracket has changed)
      let size = 1
      let i = valueIndex
      while (i + 1 < values.length &&
        values[i + 1].key[fieldIndex] === values[i].key[fieldIndex] &&
        (fieldIndex === 0 || (i + 1 < values.length && this.spanSize(values, i + 1, fieldIndex - 1) === 0))) {
        i++
        size++
      }

      return size
    },
    // Update rows/cols/valuesMap (optional)
    // @param {boolean} updateValuesMap
    updateValues: function(updateValuesMap = true) {
      this.isDataComputing = true

      // Start a task to avoid blocking the page
      clearInterval(this.computingInterval)
      this.computingInterval = setTimeout(() => {
        const rows = []
        const cols = []
        const valuesMap = new ManyKeysMap()
        const labelsMap = new ManyKeysMap()
        const itemsMap = new ManyKeysMap()

        const fields = [...this.rowFields, ...this.colFields]

        this.data.forEach(item => {
          const rowKey = []
          const colKey = []
          let filtered = false

          // Check if item passes fields value filters
          for (let field of fields) {
            if (field.valuesFiltered && !field.valuesFiltered.has(field.getter(item))) {
              filtered = true
              break
            }
          }

          // Build item rowKey/colKey if necessary
          if (!filtered || updateValuesMap) {
            this.rowFields.forEach(field => {
              rowKey.push(field.getter(item))
            })

            this.colFields.forEach(field => {
              colKey.push(field.getter(item))
            })
          }

          // Update rows/cols
          if (!filtered) {
            const rowIndex = rows.length === 0 ? -1 : rows.findIndex(row => {
              return this.rowFields.every((rowField, index) => row.key[index] === rowKey[index])
            })
            if (rowIndex === -1) {
              rows.push({ key: rowKey, items: [item] })
            } else {
              rows[rowIndex].items.push(item)
            }

            const colIndex = cols.length === 0 ? -1 : cols.findIndex(col => {
              return this.colFields.every((colField, index) => col.key[index] === colKey[index])
            })
            if (colIndex === -1) {
              cols.push({ key: colKey, items: [item] })
            } else {
              cols[colIndex].items.push(item)
            }
          }

          // Update valuesMap
          if (updateValuesMap) {
            const key = [ ...rowKey, ...colKey ]
            const value = valuesMap.get(key);
            const previousValue = !isUndefinedOrNull(value) ? value : cloneDeep(this.reducerInitialValue)

            valuesMap.set(key, this.reducer(previousValue, item))

            const previousItemsValue = itemsMap.get(key) || []

            itemsMap.set(key, [...previousItemsValue, item])
          }
        })

        // Update labelsMap
        rows.forEach(row => {
          cols.forEach(col => {
            const key = [ ...row.key, ...col.key ]
            const labels = fields.map((field, fieldIndex) => ({
              field,
              value: key[fieldIndex]
            }))
            labelsMap.set(key, labels)
          })
        })

        this.rows = rows
        this.cols = cols
        if (updateValuesMap) {
          this.valuesMap = valuesMap
          this.itemsMap = itemsMap
        }
        this.labelsMap = labelsMap

        this.isDataComputing = false
        this.updateInternalFields()
      }, 0)
    },
    // Update internal fields only
    updateInternalFields: function() {
      this.internalRowFields = cloneDeep(this.rowFields)
      this.internalColFields = cloneDeep(this.colFields)
    },
    mergeItems: function(arr, start, end) {
      return arr.slice(start, end).reduce((result, row) => {
        result = [ ...result, ...row.items ]
        return result
      }, []);
    },
    sumColspans: function(cols, colIndex) {
      return cols.slice(0, colIndex).reduce((result, col) => {
        return result + col.colspan
      }, 0)
    },
    /**
     * row should be always visible when:
     * 1. virtualStart undefined (not using virtualScroll)
     * 2. the row is under edit
     * 3. the row is between the visible range
     * @param {*} rowIndex 
     */
    isRowVisible: function(rowIndex) {
      return this.virtualStart === undefined
      || rowIndex === this.editingIndex
      || rowIndex >= this.virtualStart && rowIndex <= this.virtualEnd;
    },
    spanCheck: function(rowHeaderFields) {
      return rowHeaderFields.find((rowField) => (rowField.rowspan > 1))
    },
  },
  watch: {
    // Check if fields changed
    fields: function(val, oldVal) {
      const equalRows = val[0][0].every((item, index) => isEqual(omit(item, 'getter'), omit(oldVal[0][0][index], 'getter')));
      const equalCols = val[0][1].every((item, index) => isEqual(omit(item, 'getter'), omit(oldVal[0][1][index], 'getter')));
      if (!equalRows || !equalCols) {
        // Fields were moved
        this.updateValues()
      } else if (!isEqual(val[1], oldVal[1])) {
        // Field header value filters changed
        this.updateInternalFields()
      } else if (!isEqual(val[2], oldVal[2])) {
        // Field header slot names changed
        this.updateValues(false)
      }
    },
    data: function() {
      this.updateValues()
    }
  },
  created: function() {
    this.updateValues()
  }
}
</script>

<style scoped>
td {
  min-width: 100px;
}

.no-data-text {
  text-align: center;
  color: rgba(0, 0, 0, 0.38);
  padding-top: 6px;
}
</style>
